import React, { useEffect, useState } from "react";
import { Box, CircularProgress, Typography } from "@material-ui/core";
import ImageIcon from "@material-ui/icons/Image";
import { lprScoringServiceInstance as lprScoringService } from "../../../services/LPRScoringService";
import { useStyles } from "./index.styles";

const readBlobAsDataUrl = (blob) =>
  new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => resolve(null);
    reader.readAsDataURL(blob);
  });

/**
 * Presents the current read for scoring (AO-33366), laid out per the mockup:
 * a small cropped plate (capture) image with the recognized plate text and
 * confidence to its right, and a large context image below. A missing image
 * renders a placeholder rather than a broken image (Req 3.3).
 */
const LPRScoringCard = ({ read }) => {
  const classes = useStyles();

  const [captureImageUrl, setCaptureImageUrl] = useState(null);
  const [contextImageUrl, setContextImageUrl] = useState(null);
  const [isLoadingImages, setIsLoadingImages] = useState(false);

  useEffect(() => {
    let cancelled = false;

    if (!read) {
      setCaptureImageUrl(null);
      setContextImageUrl(null);
      return undefined;
    }

    setCaptureImageUrl(null);
    setContextImageUrl(null);
    setIsLoadingImages(true);

    (async () => {
      const [captureResponse, contextResponse] = await Promise.allSettled([
        read.isCaptureImageAvailable
          ? lprScoringService.getCaptureImage(read.lprId)
          : Promise.reject("no capture image"),
        read.isContextImageAvailable
          ? lprScoringService.getContextImage(read.lprId)
          : Promise.reject("no context image"),
      ]);

      if (cancelled) return;

      if (captureResponse.status === "fulfilled" && captureResponse.value?.data) {
        const url = await readBlobAsDataUrl(captureResponse.value.data);
        if (!cancelled) setCaptureImageUrl(url);
      }
      if (contextResponse.status === "fulfilled" && contextResponse.value?.data) {
        const url = await readBlobAsDataUrl(contextResponse.value.data);
        if (!cancelled) setContextImageUrl(url);
      }

      if (!cancelled) setIsLoadingImages(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [read]);

  if (!read) {
    return (
      <Box textAlign="center" py={6}>
        <Typography variant="body1" color="textSecondary">
          No read selected.
        </Typography>
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        {captureImageUrl ? (
          <img
            src={captureImageUrl}
            alt="Plate"
            className={classes.plateImage}
            data-testid="lpr-scoring-capture-image"
          />
        ) : (
          <Box className={classes.platePlaceholder} data-testid="lpr-scoring-capture-placeholder">
            {isLoadingImages ? <CircularProgress size={18} /> : <ImageIcon fontSize="small" />}
          </Box>
        )}
        <Box>
          <Typography variant="h5" className={classes.plateText} data-testid="lpr-scoring-plate-text">
            {read.plateText || "—"}
          </Typography>
          <Typography variant="caption" className={classes.confidence} data-testid="lpr-scoring-confidence">
            Confidence: {read.confidence}%
          </Typography>
        </Box>
      </Box>

      {contextImageUrl ? (
        <img
          src={contextImageUrl}
          alt="Context"
          className={classes.contextImage}
          data-testid="lpr-scoring-context-image"
        />
      ) : (
        <Box className={classes.contextPlaceholder} data-testid="lpr-scoring-context-placeholder">
          {isLoadingImages ? <CircularProgress size={32} /> : <ImageIcon style={{ fontSize: 64 }} />}
        </Box>
      )}
    </Box>
  );
};

export default LPRScoringCard;
